import { useEffect, useMemo, useRef, useState } from "react";
import { fetchMailMessageAttachmentFlags } from "@/lib/api/imap";
import {
  mergeAttachmentFlagsCache,
  readAttachmentFlagsCache,
} from "@/lib/cache/attachmentFlagsCache";
import type { MailMessageSummary } from "@/lib/types/mail";

function flagsFromMessages(messages: MailMessageSummary[]): Map<number, boolean> {
  const map = new Map<number, boolean>();
  for (const msg of messages) {
    if (msg.uid > 0 && msg.hasAttachments !== undefined) {
      map.set(msg.uid, msg.hasAttachments === true);
    }
  }
  return map;
}

function mergeFlagMaps(...maps: Map<number, boolean>[]): Map<number, boolean> {
  const next = new Map<number, boolean>();
  for (const map of maps) {
    for (const [uid, value] of map) {
      next.set(uid, value);
    }
  }
  return next;
}

function messageHasAttachments(
  msg: MailMessageSummary,
  flagsByUid: Map<number, boolean>
): boolean {
  if (msg.hasAttachments === true) return true;
  if (msg.hasAttachments === false) return false;
  return flagsByUid.get(msg.uid) === true;
}

/**
 * Icône PJ dans la liste — affichage instantané depuis cache local / headers DD02,
 * complété par un batch IMAP uniquement pour les UIDs encore inconnus.
 */
export function useInboxAttachmentFlags(
  accountId: number | undefined,
  folder: string,
  messages: MailMessageSummary[]
) {
  const [fetchedFlags, setFetchedFlags] = useState<Map<number, boolean>>(() => new Map());
  const fetchedUidsRef = useRef<Set<number>>(new Set());

  const listUids = useMemo(
    () => messages.filter((m) => m.uid > 0).map((m) => m.uid),
    [messages]
  );

  const listUidKey = listUids.join(",");

  const storedFlags = useMemo(
    () => (accountId != null ? readAttachmentFlagsCache(accountId, folder) : new Map()),
    [accountId, folder]
  );

  const knownFromList = useMemo(() => flagsFromMessages(messages), [messages]);

  const effectiveFlags = useMemo(
    () => mergeFlagMaps(storedFlags, fetchedFlags, knownFromList),
    [storedFlags, fetchedFlags, knownFromList]
  );

  useEffect(() => {
    fetchedUidsRef.current = new Set();
    setFetchedFlags(new Map());
  }, [accountId, folder]);

  useEffect(() => {
    if (!accountId) return;
    if (knownFromList.size === 0) return;
    mergeAttachmentFlagsCache(accountId, folder, knownFromList);
  }, [accountId, folder, knownFromList]);

  useEffect(() => {
    if (!accountId || listUids.length === 0) {
      return;
    }

    const uidsToFetch = listUids.filter((uid) => {
      if (fetchedUidsRef.current.has(uid)) return false;
      if (effectiveFlags.has(uid)) return false;
      return true;
    });

    if (uidsToFetch.length === 0) {
      return;
    }

    let cancelled = false;

    void (async () => {
      const res = await fetchMailMessageAttachmentFlags({
        accountId,
        folder,
        uids: uidsToFetch,
      });
      if (cancelled) return;
      if (!res.ok) return;

      fetchedUidsRef.current = new Set([...fetchedUidsRef.current, ...uidsToFetch]);

      const fetched = new Map<number, boolean>();
      for (const item of res.data.flags ?? []) {
        if (item.uid > 0) {
          fetched.set(item.uid, item.hasAttachments);
        }
      }
      if (fetched.size === 0) return;

      mergeAttachmentFlagsCache(accountId, folder, fetched);
      setFetchedFlags((prev) => mergeFlagMaps(prev, fetched));
    })();

    return () => {
      cancelled = true;
    };
  }, [accountId, effectiveFlags, folder, listUidKey, listUids]);

  return useMemo(
    () =>
      messages.map((msg) => {
        if (msg.uid <= 0) {
          return { ...msg, hasAttachments: false };
        }
        const hasAttachments = messageHasAttachments(msg, effectiveFlags);
        return { ...msg, hasAttachments };
      }),
    [messages, effectiveFlags]
  );
}
